/** Fixed vocabulary and field limits shared by the client, the API and the importer. */

/** One status set for cards, mindmap nodes and workflow nodes. */
export const STATUSES = ['new', 'planned', 'in_progress', 'testing', 'blocked', 'done'] as const
export type ItemStatus = (typeof STATUSES)[number]

export const STATUS_LABELS: Record<ItemStatus, string> = {
  new: 'New',
  planned: 'Planned',
  in_progress: 'In Progress',
  testing: 'Testing',
  blocked: 'Blocked',
  done: 'Done',
}

/** The dot drawn for a status on a mindmap or workflow node. */
export const STATUS_COLOURS: Record<ItemStatus, string> = {
  new: '#bdc3c7',
  planned: '#95a5a6',
  in_progress: '#f39c12',
  testing: '#3498db',
  blocked: '#e74c3c',
  done: '#2ecc71',
}

/**
 * What an item represents, whatever board it is on. Optional: meaning, not presentation —
 * a workflow node's shape (start, step, decision, end) is separate.
 */
export const ITEM_TYPES = ['story', 'task', 'decision', 'concept', 'system', 'actor', 'entity'] as const
export type ItemType = (typeof ITEM_TYPES)[number]

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  story: 'Story',
  task: 'Task',
  decision: 'Decision',
  concept: 'Concept',
  system: 'System',
  actor: 'Actor',
  entity: 'Entity',
}

/**
 * Optional icon on a card or node, picked from a fixed set. The first three are the
 * traffic-light signals; the rest are plain pictograms. A note has its own marker, so it
 * is not one.
 */
export const ICONS = [
  'danger',
  'warning',
  'ok',
  'home',
  'trash',
  'form',
  'user',
  'lock',
  'star',
  'flag',
  'bug',
  'idea',
  'question',
  'calendar',
  'mail',
  'database',
  'search',
  'clock',
  'link',
] as const
export type ItemIcon = (typeof ICONS)[number]

export const ICON_LABELS: Record<ItemIcon, string> = {
  danger: 'Danger',
  warning: 'Warning',
  ok: 'OK',
  home: 'Home',
  trash: 'Trash',
  form: 'Form',
  user: 'User',
  lock: 'Lock',
  star: 'Star',
  flag: 'Flag',
  bug: 'Bug',
  idea: 'Idea',
  question: 'Question',
  calendar: 'Calendar',
  mail: 'Mail',
  database: 'Database',
  search: 'Search',
  clock: 'Clock',
  link: 'Link',
}

/** Ranked high to low. Null means not set, which is the default for every card. */
export const CARD_PRIORITIES = ['high', 'medium', 'low'] as const
export type CardPriority = (typeof CARD_PRIORITIES)[number]

export const CARD_PRIORITY_LABELS: Record<CardPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

/**
 * MoSCoW. "Won't-have" means deliberately out of scope for now, which is a statement
 * about scope rather than the lowest rung of the priority scale above.
 */
export const MOSCOW_LEVELS = ['must_have', 'should_have', 'could_have', 'wont_have'] as const
export type MoscowLevel = (typeof MOSCOW_LEVELS)[number]

export const MOSCOW_LABELS: Record<MoscowLevel, string> = {
  must_have: 'Must-have',
  should_have: 'Should-have',
  could_have: 'Could-have',
  wont_have: "Won't-have",
}

/**
 * Sixteen colours plus the default. Ordered around the wheel — warm, then cool, then the
 * neutrals — so the picker reads as a palette rather than a list, and two adjacent
 * choices are always visibly different.
 */
export const CARD_COLOURS = [
  'default',
  'red',
  'rose',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'brown',
  'slate',
] as const
export type CardColour = (typeof CARD_COLOURS)[number]

export const CARD_COLOUR_LABELS: Record<CardColour, string> = {
  default: 'Default',
  red: 'Red',
  rose: 'Rose',
  orange: 'Orange',
  amber: 'Amber',
  yellow: 'Yellow',
  lime: 'Lime',
  green: 'Green',
  teal: 'Teal',
  cyan: 'Cyan',
  sky: 'Sky',
  blue: 'Blue',
  indigo: 'Indigo',
  violet: 'Violet',
  purple: 'Purple',
  brown: 'Brown',
  slate: 'Slate',
}

/**
 * Reference colours for the card palette. The mindmap's swatches are these, so a node's
 * colour matches a card colour exactly; other hex colours are matched to the nearest.
 */
export const CARD_COLOUR_HEX: Record<Exclude<CardColour, 'default'>, string> = {
  red: '#ef4444',
  rose: '#f43f5e',
  orange: '#f97316',
  amber: '#f59e0b',
  yellow: '#eab308',
  lime: '#84cc16',
  green: '#22c55e',
  teal: '#14b8a6',
  cyan: '#06b6d4',
  sky: '#0ea5e9',
  blue: '#3b82f6',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  purple: '#a855f7',
  brown: '#92400e',
  slate: '#64748b',
}

export const LIMITS = {
  /** Board names and every structure title. */
  title: 200,
  cardTitle: 200,
  description: 20_000,
  owner: 100,
  release: 100,
  tag: 40,
  tagCount: 20,
  /** Undoable actions retained per active board. */
  undoDepth: 50,
  /** Import guard rails. */
  importBytes: 10 * 1024 * 1024,
  importCards: 5_000,
} as const

/** Debounce before an autosave batch leaves the queue. */
export const SAVE_DEBOUNCE_MS = 400

// --- In-app feedback (docs/feedback-mechanism-porting-guide.md) -------------------------
//
// The Feedback button and its popup are Storymap's; the question for each page and every
// submission are Teamcentric Accounts' (docs/accounts-integration-dropin.md section 13.2).

/**
 * The page a submission is about. Storymap has no router, so a page is the view the app
 * shows: the board list, a story map, a mind map, a workflow, or Users.
 */
export const FEEDBACK_PAGE_KEYS = ['/boards', '/board', '/mindmap', '/workflow', '/users'] as const
export type FeedbackPageKey = (typeof FEEDBACK_PAGE_KEYS)[number]

/** How the sender characterises the note. Optional. */
export const FEEDBACK_CATEGORIES = ['FEEDBACK', 'PROBLEM', 'QUESTION'] as const
export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]

/** Asked when Accounts has no question for the page, or cannot answer. */
export const GENERIC_FEEDBACK_QUESTION = 'What would you like us to know about this page?'

export const MAX_FEEDBACK_MESSAGE_LENGTH = 2000

/**
 * One optional screenshot. SVG (script-bearing), GIF and PDF are refused: PNG, JPEG and
 * WebP cover every screenshot a browser or operating system makes.
 */
export const FEEDBACK_SCREENSHOT_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const
/** On the decoded bytes, not the base64 payload. */
export const FEEDBACK_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024
/** Longest edge kept after re-encoding; a larger screenshot is downscaled, not refused. */
export const FEEDBACK_SCREENSHOT_MAX_EDGE = 3000
/** Against decompression bombs, checked on the header before any pixels are decoded. */
export const FEEDBACK_SCREENSHOT_MAX_INPUT_EDGE = 20_000
export const FEEDBACK_SCREENSHOT_MAX_INPUT_PIXELS = 40_000_000
/** Base64 adds a third, and the data-URL prefix a few characters. */
export const FEEDBACK_SCREENSHOT_MAX_ENCODED_LENGTH = Math.ceil((FEEDBACK_SCREENSHOT_MAX_BYTES * 4) / 3) + 128
export const FEEDBACK_SCREENSHOT_RULE_TEXT = 'One PNG, JPEG, or WebP image up to 5 MB.'
export const FEEDBACK_SCREENSHOT_WARNING =
  'Remove passwords, customer data, source code, and other sensitive information before attaching a screenshot.'
